import { Button, FormHelperText, Stack, Typography } from '@mui/material'
import cloneDeep from 'lodash/cloneDeep'
import { forwardRef, useMemo, useState } from 'react'
import {
  FieldError,
  FieldPath,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form'
import {
  DateTimePickerField,
  Fade,
  FileUploader,
  MaskedInputField,
  MaskedInputFieldProps,
  Resource,
  TextInputField,
  TextInputFieldProps,
  ToggleInputField,
} from 'utoniq-ui'

import {
  CreateMissionInput,
  GpsDataInput,
  Maybe,
  MissionType,
} from '@/clients/utoniq-core/schema'
import {
  ItemSelectBox,
  ItemSelectBoxProps,
} from '@/components/pages/ConsoleMissionCreate/template/ItemSelectBox'
import {
  Form,
  handleError,
  tLabel,
  useForm,
  UseFormProps,
  useMessage,
  useTranslation,
} from '@/core'
import { Cell, CellFrame, Organism } from '@/core/architecture'
import { convertDatePickerValueToDate, dayjs } from '@/lib/dayjs'

import { GPSMapModal, GPSMapModalProps } from './GPSMapModal'
import { ItemSelectModal, ItemSelectModalProps } from './ItemSelectModal'

export type MissionCreateFormProps = Pick<
  ItemSelectModalProps,
  'loading' | 'hasNextPage' | 'items' | 'onFetchMore' | 'onSearch' | 'artistId'
> & {
  missionType: MissionType
  onSubmit: (values: CreateMissionProps) => Promise<void>
  fileUploadInfo: FileUploadInfo
  gpsClientInfo: {
    permission: boolean
    currentLocation?: GPSMapModalProps['currentData']
  }
  onPlaceSearch: GPSMapModalProps['onSearch']
  predictions: GPSMapModalProps['predictions']
  place: GPSMapModalProps['place']
  onSelectPlace: GPSMapModalProps['onSelectPlace']
  onRequestGPSPermission: () => void
  removePlace: () => void
}

export type CreateMissionProps = Omit<CreateMissionInput, 'gpsData'> & {
  hasCheckIn: boolean
  startedAt: string | null
  contentResource: Resource | null
  isNoQuiz?: boolean
  gpsData?: Maybe<Omit<GpsDataInput, 'radius'> & { radius: string | number }>
}

export type FileUploadInfo = {
  onDrop: () => Promise<Resource | undefined>
  progress: number
}

const schema: UseFormProps['schema'] = o => ({
  title: o.string().max(50).label(tLabel('consoleMissionCreate.title')),
  description: o
    .string()
    .max(500)
    .label(tLabel('consoleMissionCreate.description')),
  content: o.object().when('type', {
    is: MissionType.Quiz,
    then: o.object().when('isNoQuiz', {
      is: true,
      then: o.object({
        image: o.object({
          uri: o
            .string()
            .allow('')
            .optional()
            .label(tLabel('consoleMissionEdit.content.quiz.image.uri')),
        }),
        answer: o
          .string()
          .max(50)
          .allow('')
          .optional()
          .label(tLabel('consoleMissionEdit.content.quiz.answer')),
      }),
      otherwise: o.object({
        image: o.object({
          uri: o
            .string()
            .label(tLabel('consoleMissionEdit.content.quiz.image.uri')),
        }),
        answer: o
          .string()
          .max(50)
          .label(tLabel('consoleMissionEdit.content.quiz.answer')),
      }),
    }),
    otherwise: o.object().allow(null).optional(),
  }),
  startedAt: o
    .date()
    .allow(null)
    .optional()
    .label(tLabel('consoleMissionCreate.startedAt')),
  expiredAt: o
    .date()
    .optional()
    .label(tLabel('consoleMissionCreate.expiredAt')),
  type: o.string(),
  isPublished: o.boolean(),
  reward: o.object({
    item: o
      .object({
        id: o.string().label(tLabel('consoleMissionCreate.reward.item.id')),
      })
      .allow(null)
      .optional(),
    gachaTicket: o
      .object({
        gachaTicketId: o.string(),
        count: o.number(),
      })
      .allow(null)
      .optional(),
  }),
  gpsData: o.object().when('hasCheckIn', {
    is: true,
    then: o.object({
      latitude: o
        .number()
        .label(tLabel('consoleMissionCreate.gpsData.latitude')),
      longitude: o
        .number()
        .label(tLabel('consoleMissionCreate.gpsData.longitude')),
      approximateAddress: o
        .string()
        .label(tLabel('consoleMissionCreate.gpsData.approximateAddress')),
      radius: o.string().label(tLabel('consoleMissionCreate.gpsData.radius')),
    }),
    otherwise: o.object().allow(null).optional(),
  }),
  //local field
  isNoQuiz: o.boolean().optional(),
  contentResource: o.object().allow(null).optional(),
  hasCheckIn: o.boolean().allow(false).optional(),
})

export const MissionCreateForm: Organism<MissionCreateFormProps> = ({
  loading,
  artistId,
  items,
  hasNextPage,
  onFetchMore,
  onSubmit,
  missionType,
  onSearch,
  onPlaceSearch,
  predictions,
  place,
  onSelectPlace,
  fileUploadInfo,
  gpsClientInfo,
  onRequestGPSPermission,
  removePlace,
}) => {
  const { t } = useTranslation('consoleMissionCreate')
  const { showError } = useMessage()
  const {
    handleSubmit,
    formState: { isSubmitting, errors, isValid },
    register,
    setValue,
    watch,
    setError,
    trigger,
  } = useForm<CreateMissionProps>({
    schema,
    defaultValues: {
      ...defaultValues,
      type: missionType,
    },
  })
  const { ref: isNoQuizRef, ...restIsNoQuiz } = register('isNoQuiz')
  const isNoQuizWatch = watch('isNoQuiz')

  const handleOnSubmit = handleSubmit(
    async ({ hasCheckIn, gpsData, content, isNoQuiz, ...values }) => {
      await onSubmit({
        ...values,
        hasCheckIn,
        gpsData: hasCheckIn ? gpsData : null,
        content: {
          ...content,
          image: !isNoQuiz ? content.image : { uri: '' },
          answer: !isNoQuiz ? content.answer : '',
        },
      }).catch(error => {
        handleError<CreateMissionProps>(error, setError, showError)
      })
    }
  )

  return (
    <Form sx={{ p: 5 }}>
      <TextInputCell type="text" {...register('title')} error={errors.title} />
      <TextInputCell
        type="textarea"
        textFieldProps={{
          multiline: true,
          rows: 5,
          InputProps: { sx: { px: 4, py: 3 } },
        }}
        {...register('description')}
        error={errors.description}
      />
      <CheckInInputCell
        setValue={setValue}
        artistId={artistId}
        value={watch('gpsData')}
        hasCheckIn={watch('hasCheckIn')}
        gpsClientInfo={gpsClientInfo}
        onPlaceSearch={onPlaceSearch}
        predictions={predictions}
        place={place}
        onSelectPlace={onSelectPlace}
        onRequestGPSPermission={onRequestGPSPermission}
        removePlace={removePlace}
        inputProps={{
          type: 'text',
          error: errors.gpsData?.radius,
          mask: '00000m',
          ...register('gpsData.radius'),
        }}
        inputApproximateAddressProps={{
          type: 'text',
          error: errors.gpsData?.approximateAddress,
          ...register('gpsData.approximateAddress'),
        }}
      />
      <ToggleInputField
        content={{
          title: t('noQuizTitle'),
          description: (
            <Typography variant="body2" color="text.secondary">
              {t('noQuizNote')}
            </Typography>
          ),
        }}
        checked={isNoQuizWatch}
        inputRef={isNoQuizRef}
        sx={{ mx: -2, mt: 4, mb: -5 }}
        {...restIsNoQuiz}
        onChange={async event => {
          setValue('isNoQuiz', event.target.checked, {
            shouldValidate: true,
            shouldDirty: true,
          })
          await trigger(['content.answer', 'content.image.uri'])
        }}
      />
      <MissionImageCell
        watch={watch}
        setValue={setValue}
        disabled={isNoQuizWatch}
        error={errors.content?.image?.uri}
        fileUploadInfo={fileUploadInfo}
      />
      <MissionAnswerCell
        inputProps={{
          disabled: isNoQuizWatch,
          type: 'text',
          error: errors.content?.answer,
          ...register('content.answer'),
        }}
      />
      <RewardCell
        items={items}
        artistId={artistId}
        value={watch('reward.item.id')}
        loading={loading}
        setValue={setValue}
        hasNextPage={hasNextPage}
        onFetchMore={onFetchMore}
        onSearch={onSearch}
      />
      <DatePickerCell
        name="startedAt"
        error={errors.startedAt}
        setValue={setValue}
        value={watch('startedAt')}
      />
      <DatePickerCell
        name="expiredAt"
        error={errors.expiredAt}
        setValue={setValue}
        value={watch('expiredAt')}
      />
      <Stack spacing={5} alignItems="center">
        <Button
          size="large"
          sx={{ width: 244 }}
          disabled={!isValid || isSubmitting}
          loading={isSubmitting}
          onClick={handleOnSubmit}
        >
          {t('create')}
        </Button>
        {/**
         * @description
         * Open when function is implemented in the future
         * https://app.clickup.com/t/908127/UT-5944
         */}
        {/*<Button*/}
        {/*  variant="outlined"*/}
        {/*  size="large"*/}
        {/*  sx={{ width: 244 }}*/}
        {/*  disabled={!isValid || isSubmitting}*/}
        {/*>*/}
        {/*  {t('temporarilySave')}*/}
        {/*</Button>*/}
      </Stack>
    </Form>
  )
}

type CheckInInputCellProps = {
  artistId: string
  setValue: UseFormSetValue<CreateMissionProps>
  hasCheckIn: boolean
  value: CreateMissionProps['gpsData']
  inputProps: Omit<MaskedInputFieldProps, 't'>
  inputApproximateAddressProps: Omit<TextInputFieldProps, 't'>
  gpsClientInfo: {
    permission: boolean
    currentLocation?: GPSMapModalProps['currentData']
  }
  onPlaceSearch: GPSMapModalProps['onSearch']
  predictions: GPSMapModalProps['predictions']
  place: GPSMapModalProps['place']
  onSelectPlace: GPSMapModalProps['onSelectPlace']
  onRequestGPSPermission: () => void
  removePlace: () => void
}
export const CheckInInputCell: Cell<CheckInInputCellProps> = ({
  artistId,
  hasCheckIn,
  setValue,
  value,
  inputProps,
  inputApproximateAddressProps,
  onPlaceSearch,
  gpsClientInfo,
  predictions,
  place,
  onSelectPlace,
  onRequestGPSPermission,
  removePlace,
}) => {
  const { t } = useTranslation('consoleMissionCreate')
  const [open, setOpen] = useState<boolean>(false)
  const options = [
    { value: true, label: t('specified') },
    { value: false, label: t('unspecified') },
  ]

  const currentPlace = useMemo<GPSMapModalProps['place']>(() => {
    if (place) {
      return place
    }
    if (value?.latitude && value?.longitude) {
      return {
        formattedAddress: value.approximateAddress ?? '',
        location: {
          lat: value.latitude,
          lng: value.longitude,
        },
      }
    }
    return null
  }, [place, value])

  return (
    <CellFrame>
      <Typography variant="h5">{t('checkInLabel')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ pt: 2 }}>
        {t('checkInDesc')}
      </Typography>
      <Stack direction="row" spacing={2} sx={{ py: 2, mb: 3 }}>
        {options.map((option, index) => (
          <Button
            key={index}
            variant={hasCheckIn === option.value ? 'contained' : 'outlined'}
            onClick={() => {
              if (option.value) {
                onRequestGPSPermission()
              }
              setValue('hasCheckIn', option.value, {
                shouldValidate: true,
                shouldDirty: true,
              })
              if (!option.value) {
                setValue(
                  'gpsData',
                  {
                    latitude: 0,
                    longitude: 0,
                    radius: '',
                    approximateAddress: '',
                  },
                  {
                    shouldValidate: true,
                    shouldDirty: true,
                  }
                )
                removePlace()
              }
            }}
          >
            {option.label}
          </Button>
        ))}
      </Stack>
      {hasCheckIn && (
        <Fade>
          <>
            <Stack sx={{ pb: 8, pt: 4 }}>
              <Typography variant="h5">{t('positionLabel')}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ pt: 2 }}>
                {t('positionDesc')}
              </Typography>
              <Button
                variant="text"
                sx={{ width: 'fit-content', mx: '-6px' }}
                onClick={() => setOpen(true)}
              >
                <Typography variant="subtitle1" color="inherit">
                  {t('specifiedMap')}
                </Typography>
              </Button>
              {!!value?.latitude && !!value?.longitude && (
                <TextInputCell
                  textFieldProps={{ sx: { mb: 0 } }}
                  {...inputApproximateAddressProps}
                />
              )}
            </Stack>
            <Stack>
              <Typography variant="h5">{t('radiusLabel')}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ pt: 2 }}>
                {t('radiusDesc')}
              </Typography>
              <MaskedInputCell
                {...inputProps}
                inputProps={{
                  pattern: '[0-9/]*',
                  inputMode: 'numeric',
                }}
                type="tel"
              />
            </Stack>
            <GPSMapModal
              currentData={gpsClientInfo?.currentLocation}
              artistId={artistId}
              onSearch={onPlaceSearch}
              predictions={predictions}
              onSelectPlace={onSelectPlace}
              place={currentPlace}
              open={open}
              onSpecify={({ latitude, longitude, formattedAddress }) => {
                setValue(
                  'gpsData.approximateAddress',
                  formattedAddress as never,
                  { shouldValidate: true, shouldDirty: true }
                )
                setValue('gpsData.latitude', latitude as never, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
                setValue('gpsData.longitude', longitude as never, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
                setOpen(false)
              }}
              onClose={() => setOpen(false)}
            />
          </>
        </Fade>
      )}
    </CellFrame>
  )
}

type MissionImageCellProps = {
  fileUploadInfo: FileUploadInfo
  watch: UseFormWatch<CreateMissionProps>
  error?: FieldError
  setValue: UseFormSetValue<CreateMissionProps>
  disabled?: boolean
}
export const MissionImageCell: Cell<MissionImageCellProps> = ({
  fileUploadInfo,
  error,
  watch,
  setValue,
  disabled,
}) => {
  const { t } = useTranslation('consoleMissionCreate')

  return (
    <CellFrame sx={{ pb: 5 }}>
      <FileUploader
        t={t}
        disabled={disabled}
        resource={
          watch('contentResource.imageUrl')
            ? watch('contentResource') ?? undefined
            : undefined
        }
        fileUploadInfo={{
          type: 'image',
          onDrop() {
            return fileUploadInfo.onDrop().then(newResource => {
              if (!newResource?.imageUploadPath) {
                return
              }
              setValue(
                'content.image.uri',
                newResource.imageUploadPath as never,
                {
                  shouldValidate: true,
                  shouldDirty: true,
                }
              )
              setValue('contentResource', cloneDeep(newResource))
              return cloneDeep(newResource)
            })
          },
          progress: fileUploadInfo.progress || 0,
          description: t('imageUploaderDescription'),
          onClose() {
            setValue('content.image.uri', '' as never, {
              shouldValidate: true,
              shouldDirty: true,
            })
            setValue('contentResource', null)
          },
        }}
        content={{ title: t('quizAddImage') }}
      />
      {!!error && <FormHelperText error>{error.message}</FormHelperText>}
    </CellFrame>
  )
}

type MissionAnswerCellProps = {
  inputProps: Omit<TextInputFieldProps, 't'>
}
export const MissionAnswerCell: Cell<MissionAnswerCellProps> = ({
  inputProps,
}) => {
  const { t } = useTranslation('consoleMissionCreate')
  return (
    <CellFrame sx={{ pb: 5 }}>
      <Typography
        variant="h5"
        color={inputProps.disabled ? 'text.disabled' : 'text.primary'}
      >
        {t('answerLabel')}
      </Typography>
      <Typography
        variant="body2"
        color={inputProps.disabled ? 'text.disabled' : 'text.secondary'}
        sx={{ pt: 2 }}
      >
        {t('answerDesc')}
      </Typography>
      <TextInputCell textFieldProps={{ sx: { mb: 0 } }} {...inputProps} />
    </CellFrame>
  )
}

type RewardCellProps = Pick<
  ItemSelectModalProps,
  'loading' | 'hasNextPage' | 'items' | 'onFetchMore' | 'onSearch' | 'artistId'
> & {
  value?: string
  defaultItem?: ItemSelectBoxProps['item']
  setValue: UseFormSetValue<CreateMissionProps>
}
export const RewardCell: Cell<RewardCellProps> = ({
  artistId,
  value,
  defaultItem,
  loading,
  hasNextPage,
  items,
  onFetchMore,
  onSearch,
  setValue,
}) => {
  const { t } = useTranslation('consoleMissionCreate')
  const [open, setOpen] = useState<boolean>(false)
  const [itemSelected, setItemSelected] = useState<
    ItemSelectBoxProps['item'] | undefined
  >(defaultItem)

  return (
    <CellFrame sx={{ pb: 8 }}>
      <Typography variant="h5">{t('rewardLabel')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ pt: 2 }}>
        {t('rewardDesc')}
      </Typography>
      {itemSelected ? (
        <ItemSelectBox
          item={itemSelected}
          onClose={() => {
            setItemSelected(undefined)
            setValue('reward.item.id', '' as never, {
              shouldValidate: true,
              shouldDirty: true,
            })
          }}
        />
      ) : (
        <Button
          variant="outlined"
          sx={{ mt: 4 }}
          onClick={() => {
            setOpen(true)
            return onSearch({ keyword: '' })
          }}
        >
          {t('add')}
        </Button>
      )}
      <ItemSelectModal
        open={open}
        artistId={artistId}
        onClose={() => setOpen(false)}
        value={value ?? ''}
        onSelect={id => {
          setItemSelected(items?.edges.find(({ node }) => node.id === id)?.node)
          setOpen(false)
          setValue('reward.item.id', id as never, {
            shouldValidate: true,
            shouldDirty: true,
          })
        }}
        items={items}
        loading={loading}
        hasNextPage={hasNextPage}
        onFetchMore={onFetchMore}
        onSearch={onSearch}
      />
    </CellFrame>
  )
}

export const TextInputCell = forwardRef(
  (
    {
      titleProps,
      textFieldProps,
      error,
      ...props
    }: Omit<TextInputFieldProps, 't'>,
    ref
  ) => {
    const { t } = useTranslation('consoleMissionCreate')
    return (
      <CellFrame>
        <TextInputField
          titleProps={{ ...titleProps, variant: 'h5', color: 'text.primary' }}
          textFieldProps={{
            label: error ? t('error') : '',
            ...textFieldProps,
          }}
          ref={ref}
          {...props}
          error={error}
          t={t}
        />
      </CellFrame>
    )
  }
)

export const MaskedInputCell = forwardRef(
  (
    {
      titleProps,
      textFieldProps,
      error,
      ...props
    }: Omit<MaskedInputFieldProps, 't'>,
    ref
  ) => {
    const { t } = useTranslation('consoleMissionCreate')
    return (
      <CellFrame>
        <MaskedInputField
          titleProps={{ ...titleProps, variant: 'h5', color: 'text.primary' }}
          textFieldProps={{
            label: error ? t('error') : '',
            ...textFieldProps,
          }}
          {...props}
          ref={ref}
          t={t}
          placeholder={t(`${props.name}Placeholder`)}
        />
      </CellFrame>
    )
  }
)

type DatePickerCellProps = {
  setValue: UseFormSetValue<CreateMissionProps>
  value: string | null
  name: string
  error?: FieldError
}
export const DatePickerCell: Cell<DatePickerCellProps> = ({
  setValue,
  ...props
}) => {
  const { t } = useTranslation('consoleMissionCreate')
  const value = useMemo(
    () => (props.value ? dayjs(props.value) : null),
    [props.value]
  )
  return (
    <CellFrame sx={{ pb: 5 }}>
      <DateTimePickerField
        onChange={date => {
          setValue(
            props.name as FieldPath<CreateMissionProps>,
            convertDatePickerValueToDate(date) as never,
            {
              shouldValidate: true,
              shouldDirty: true,
            }
          )
        }}
        {...props}
        value={value}
        title={t(`${props.name}Title`)}
        placeholder={t(`${props.name}Placeholder`)}
        textFieldProps={{
          label: props.error ? t('error') : '',
        }}
        titleProps={{
          variant: 'h5',
          color: 'text.primary',
        }}
      />
    </CellFrame>
  )
}

const defaultValues: CreateMissionProps = {
  title: '',
  description: '',
  type: MissionType.Quiz,
  content: {
    answer: '',
    image: {
      uri: '',
    },
  },
  contentResource: null,
  gpsData: {
    longitude: 0,
    latitude: 0,
    radius: '',
    approximateAddress: '',
  },
  isPublished: true,
  expiredAt: null,
  reward: {
    item: {
      id: '',
    },
    gachaTicket: null,
  },
  hasCheckIn: false,
  startedAt: null,
  isNoQuiz: false,
}
